import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { root } from '~/preloaded';
import { TREASURY_TABS } from '~/constants';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { supplyViewSelector } from '~/store/selectors/supplySelectors';
import { setSelectedBuildingThunk } from '~/store/thunks/supplyThunks';

import { actionsSupply } from '~/Actions/ActionSupply';

import styles from './ClanSupply.scss';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import SideBar from './Sidebar/Sidebar';
import SidebarToggleButton from './SidebarToggleButton/SidebarToggleButton';
import SupplyMap from './SupplyMap/SupplyMap';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { Nullable } from '~/types/generics';

interface ClanSupplyProps {
  clan: ClanInfoType;
  isOwnClanBase: boolean;
}

const ClanSupply = ({ clan, isOwnClanBase }: ClanSupplyProps) => {
  const {
    buildings,
    confirmBuildingId,
    isSidebarOpened,
    mapHost,
    prevSelectedBuildingId,
    selectedBuildingId,
    tempHoverBuildingId,
    tempHoverBuildingLevel,
  } = useAppSelector(supplyViewSelector);

  const battlesStats = clan?.stats || {};

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dropSelection = useCallback(() => {
    dispatch(setSelectedBuildingThunk(null));
    dispatch(actionsSupply.hideBuildingConfirm());
  }, [dispatch]);

  const navigateToTreasury = useCallback(
    (tab: TREASURY_TABS = TREASURY_TABS.REWARDS) => {
      navigate(`${root}${ROUTES_MAP.TREASURY}/${tab}`);
    },
    [navigate],
  );

  const buildingData = useMemo(() => {
    if (prevSelectedBuildingId && buildings?.[prevSelectedBuildingId]) {
      return { ...buildings[prevSelectedBuildingId] };
    }
    return null;
  }, [prevSelectedBuildingId, buildings]);

  const { title, name, bonuses } = useMemo(() => {
    let title = '';
    let name: Nullable<BUILDINGS> = null;
    let bonuses: string[] = [];

    if (!buildingData) {
      return { title, name, bonuses };
    }

    let level = buildingData.level;

    if (confirmBuildingId) {
      level += 1;
    }

    if (tempHoverBuildingLevel) {
      level = tempHoverBuildingLevel.level;
    }

    title = buildingData.levels[level]?.title || '';
    name = buildingData.name || null;

    if (level === 0) {
      bonuses = [buildingData.levels[0]?.meta?.function || ''];
    } else {
      if (name === 'superships_home') {
        bonuses = [];
        for (let i = 1; i <= level; i++) {
          bonuses.push(buildingData.levels[i]?.meta?.totalBonus || '');
        }
      } else {
        bonuses = [buildingData.levels[level]?.meta?.totalBonus || ''];
      }
    }

    return { title, name, bonuses };
  }, [buildingData, confirmBuildingId, tempHoverBuildingLevel]);

  return (
    <div className={styles.container}>
      <SupplyMap clanId={clan.id} />

      <SidebarToggleButton isSidebarOpened={isSidebarOpened} />

      <div className={styles.innerShadow} />

      <Header
        name={name}
        title={title}
        level={buildingData?.level || 0}
        maxLevel={buildingData?.maxLevel || 0}
        selectedBuildingId={selectedBuildingId}
        dropSelection={dropSelection}
      />

      <Footer
        bonuses={bonuses}
        isOwnBase={isOwnClanBase}
        level={buildingData?.level ?? null}
        confirmBuildingId={confirmBuildingId}
        selectedBuildingId={selectedBuildingId}
        tempHoverBuildingId={tempHoverBuildingId}
        dropSelection={dropSelection}
        navigateToTreasury={navigateToTreasury}
      />

      {buildings && (
        <SideBar
          battlesStats={battlesStats}
          buildings={buildings}
          confirmBuildingId={confirmBuildingId}
          isOwnBase={isOwnClanBase}
          isSidebarOpened={isSidebarOpened}
          mapHost={mapHost}
          selectedBuildingId={selectedBuildingId}
          tempHoverBuildingLevel={tempHoverBuildingLevel}
          navigateToTreasury={navigateToTreasury}
        />
      )}
    </div>
  );
};

export default memo(ClanSupply);
